import Toast from "@/components/base/Toast";
import { useState } from "react";
import { heroFeatured, heroSideCards, heroTopics, hotChatter } from "@/mocks/home";

export default function HeroFocus() {
  const [toast, setToast] = useState<string | null>(null);

  const handleRefresh = () => {
    setToast("已刷新头版焦点（演示效果）");
    window.setTimeout(() => setToast(null), 2000);
  };

  return (
    <section id="focus" className="w-full border-b border-background-200 bg-background-50">
      <div className="mx-auto w-full max-w-page px-4 py-8 md:px-6 md:py-12">
        <div className="mb-6 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-50 px-3 py-1 text-xs text-primary-700">
              <i className="ri-pushpin-2-fill text-primary-500" />
              头版焦点
            </span>
            <h2 className="mt-3 font-heading text-3xl leading-tight text-foreground-950 md:text-4xl">
              置顶爆料抢先看
            </h2>
            <p className="mt-2 max-w-2xl text-sm text-foreground-600 md:text-base">
              头版焦点把今天讨论量最高、转载最快的几条放在一起，谁在顶上、哪条被顶到最前，一眼就够。
            </p>
          </div>
          <button
            type="button"
            onClick={handleRefresh}
            className="inline-flex w-fit cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border border-background-300 bg-background-100 px-4 py-2 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
          >
            <i className="ri-refresh-line" />
            刷新焦点
          </button>
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-12 lg:gap-5">
          <article className="animate-fade-up overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300 lg:col-span-5">
            <div className="relative h-56 w-full overflow-hidden md:h-72">
              <img
                src={heroFeatured.image}
                alt={heroFeatured.imageAlt}
                className="h-full w-full object-cover object-top"
              />
              <span className="absolute left-3 top-3 inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-primary-500 px-3 py-1 text-xs text-background-50">
                <i className="ri-flashlight-fill" />
                {heroFeatured.badge}
              </span>
            </div>
            <div className="p-4 md:p-5">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-secondary-100 px-2.5 py-0.5 text-xs text-secondary-900">
                  {heroFeatured.tag}
                </span>
                <span className="flex items-center gap-1 text-xs text-foreground-500">
                  <i className="ri-eye-line" />
                  {heroFeatured.read}
                </span>
              </div>
              <h3 className="mt-2.5 font-heading text-xl leading-snug text-foreground-950 md:text-2xl">
                {heroFeatured.title}
              </h3>
              <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">
                {heroFeatured.summary}
              </p>
              <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1.5 border-t border-background-200 pt-3 text-xs text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-user-3-line" />
                  {heroFeatured.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line" />
                  {heroFeatured.time}
                </span>
                <span className="flex items-center gap-1 text-primary-600">
                  <i className="ri-chat-3-line" />
                  {heroFeatured.comments} 条讨论
                </span>
              </div>
            </div>
          </article>

          <div className="flex flex-col gap-4 lg:col-span-4 lg:gap-5">
            {heroSideCards.map((card, index) => (
              <article
                key={card.title}
                className={`animate-fade-up delay-${index + 1} group flex flex-1 cursor-pointer gap-3.5 rounded-lg border border-background-200 bg-background-50 p-3.5 transition-colors hover:border-accent-300 md:p-4`}
              >
                <div className="h-24 w-28 shrink-0 overflow-hidden rounded-md md:h-28 md:w-32">
                  <img
                    src={card.image}
                    alt={card.imageAlt}
                    className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="flex min-w-0 flex-col">
                  <span className="w-fit rounded-full bg-accent-100 px-2.5 py-0.5 text-xs text-accent-900">
                    {card.tag}
                  </span>
                  <h3 className="mt-2 line-clamp-2 font-heading text-base leading-snug text-foreground-950 md:text-lg">
                    {card.title}
                  </h3>
                  <p className="mt-1.5 line-clamp-2 text-xs leading-relaxed text-foreground-600 md:text-sm">
                    {card.summary}
                  </p>
                  <div className="mt-auto flex items-center gap-3 pt-2 text-xs text-foreground-500">
                    <span className="flex items-center gap-1">
                      <i className="ri-time-line" />
                      {card.time}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-chat-3-line" />
                      {card.comments}
                    </span>
                  </div>
                </div>
              </article>
            ))}
          </div>

          <div className="animate-fade-up delay-3 flex flex-col gap-4 rounded-lg border border-background-200 bg-background-100 p-4 lg:col-span-3 md:p-5">
            <div>
              <h3 className="flex items-center gap-2 font-heading text-lg text-foreground-950">
                <i className="ri-hashtag text-secondary-600" />
                热议话题
              </h3>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {heroTopics.map((topic) => (
                  <button
                    key={topic}
                    type="button"
                    className="cursor-pointer whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-2.5 py-1 text-xs text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
                  >
                    #{topic}
                  </button>
                ))}
              </div>
            </div>
            <div className="border-t border-background-300/70 pt-4">
              <h3 className="flex items-center gap-2 font-heading text-lg text-foreground-950">
                <i className="ri-chat-smile-2-line text-primary-500" />
                社区正在聊
              </h3>
              <ul className="mt-3 flex flex-col gap-3">
                {hotChatter.map((item) => (
                  <li key={item.text} className="flex gap-2.5">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-accent-500" />
                    <div>
                      <p className="line-clamp-2 text-xs leading-relaxed text-foreground-700">
                        {item.text}
                      </p>
                      <span className="mt-0.5 block text-[11px] text-foreground-400">
                        {item.time}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
      <Toast message={toast} />
    </section>
  );
}